import { Box } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DenyButton from "../../common/buttons/DenyButton";
import AcceptButton from "../../common/buttons/AcceptButton";
import { MedicalIndicatorsEnum } from "../../../models/enums/medicalIndicatorsEnum";
import { MedicalCheckupDto } from "../../../models/medicalCheckups/medicalCheckupDto";
import { MedicalCheckupResult } from "../../../models/enums/medicalCheckupResult";
import { MedicalCheckupVerdictDto } from "../../../models/medicalCheckups/medicalCheckupVerdictDto";
import { medicalCheckupSigningService, ValidationError, WorkplaceError, NetworkError } from "../../../services/medicalCheckupSigningService";
import { NotAllowedDialog } from "../NotAllowedDialog";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { ROUTES } from "../../../constants/routes";

interface CloudTokenSigningButtonsProps {
    medicalCheckup: MedicalCheckupDto;
    disabled?: boolean;
    onValidationError?: (message: string) => void;
    onWorkplaceError?: (message: string) => void;
    onNetworkError?: (message: string) => void;
    onLoadingChange?: (loading: boolean, spinnerText?: string) => void;
}

export const CloudTokenSigningButtons: React.FC<CloudTokenSigningButtonsProps> = ({
    medicalCheckup,
    disabled = false,
    onValidationError,
    onWorkplaceError,
    onNetworkError,
    onLoadingChange,
}) => {
    const [openNotAllowedDialog, setOpenNotAllowedDialog] = useState(false);
    const [openAllowConfirmationDialog, setOpenAllowConfirmationDialog] = useState(false);
    const navigate = useNavigate();

    const hasRedValuesOrComplaints = () => {
        if (!medicalCheckup) return false;

        // Check for complaints
        if (medicalCheckup.complaints && medicalCheckup.complaints.length > 0) {
            return true;
        }

        // Check for red medical indicators
        const indicators = medicalCheckup.indicators;
        return (
            indicators.alcoholParam === MedicalIndicatorsEnum.High ||
            indicators.upperPressureParam === MedicalIndicatorsEnum.High ||
            indicators.lowerPressureParam === MedicalIndicatorsEnum.High ||
            indicators.pulseParam === MedicalIndicatorsEnum.High ||
            indicators.temperatureParam === MedicalIndicatorsEnum.High
        );
    };

    const handleVerdict = async (verdictData: MedicalCheckupVerdictDto) => {
        onLoadingChange?.(true, "Подписание...");

        try {
            await medicalCheckupSigningService.signWithCloudCertificate(
                medicalCheckup.id,
                verdictData,
                (progress) => {
                    onLoadingChange?.(true, progress.message);
                }
            );

            // Успешное подписание
            navigate(ROUTES.medicalCheckups.queue);
        } catch (e: any) {
            console.error(e);
            
            if (e instanceof ValidationError) {
                onValidationError?.(e.message);
            } else if (e instanceof NetworkError) {
                onNetworkError?.(e.message);
            } else if (e instanceof WorkplaceError) {
                onWorkplaceError?.(e.message);
            } else {
                if (e.response) {
                    const errorMessage = e.response?.data?.message ?? e.message ?? "Ошибка валидации";
                    onValidationError?.(errorMessage);
                } else {
                    const errorMessage = e.message ?? "Непредвиденная ошибка";
                    onWorkplaceError?.(errorMessage);
                }
            }
            
            onLoadingChange?.(false);
        }
    };

    const handleAllow = () => {
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        
        // If there are red values or complaints, show confirmation dialog
        if (hasRedValuesOrComplaints()) {
            setOpenAllowConfirmationDialog(true);
        } else {
            handleVerdict(verdictData);
        }
    };

    const handleAllowConfirmed = () => {
        setOpenAllowConfirmationDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Approved,
            rejectReasons: []
        };
        handleVerdict(verdictData);
    };

    const handleNotAllowed = (selectedReasons: number[], comment: string) => {
        setOpenNotAllowedDialog(false);
        const verdictData: MedicalCheckupVerdictDto = {
            result: MedicalCheckupResult.Rejected,
            rejectReasons: selectedReasons,
            comment: comment
        };
        handleVerdict(verdictData);
    };

    return (
        <>
            <Box paddingTop={1} px={2} sx={{ display: 'flex', float: 'right' }}>
                <Box px={2}>
                    <DenyButton 
                        onClick={() => setOpenNotAllowedDialog(true)} 
                        disabled={disabled}
                    >
                        Недопуск
                    </DenyButton>
                </Box>
                <Box>
                    <AcceptButton 
                        onClick={handleAllow} 
                        disabled={medicalCheckup.indicators.alcoholParam === MedicalIndicatorsEnum.High || disabled}
                    >
                        Допуск
                    </AcceptButton>
                </Box>
            </Box>
            <NotAllowedDialog
                medicalCheckupId={medicalCheckup.id}
                open={openNotAllowedDialog}
                onSubmit={handleNotAllowed}
                onClose={() => setOpenNotAllowedDialog(false)}
            />
            <ConfirmDialog
                open={openAllowConfirmationDialog}
                title="Подтверждение допуска"
                content="У водителя есть недопустимые значения или жалобы. Вы уверены, что хотите допустить сотрудника?"
                onClose={() => setOpenAllowConfirmationDialog(false)}
                onConfirm={handleAllowConfirmed}
            />
        </>
    );
};
